import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Bell, Plus, Trash2, ExternalLink, TrendingDown, Target, LogOut, RefreshCw, X, Sparkles, Loader2, Link as LinkIcon, Flame, Store, Smartphone } from "lucide-react";
import { Link } from "react-router-dom";

const curSymbol = (c) => (c === "USD" ? "$" : c === "GBP" ? "£" : c === "EUR" ? "€" : c || "");
const pct = (a, b) => (b ? Math.round(((b - a) / b) * 1000) / 10 : 0);
const daysAgo = (iso) => {
    if (!iso) return "";
    const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
    return d === 0 ? "today" : `${d}d ago`;
};

// Compact SVG sparkline of the price history
const Sparkline = ({ points, color = "#0A0A0A" }) => {
    if (!points || points.length < 2) return null;
    const prices = points.map((p) => p.price);
    const min = Math.min(...prices), max = Math.max(...prices);
    const range = max - min || 1;
    const w = 100, h = 28;
    const step = w / (points.length - 1);
    const path = points.map((p, i) => {
        const x = i * step;
        const y = h - ((p.price - min) / range) * h;
        return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(" ");
    return (
        <svg width={w} height={h} className="opacity-70">
            <path d={path} stroke={color} strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
};

export default function Wishlist() {
    const { logout } = useAuth();
    const [items, setItems] = useState([]);
    const [alerts, setAlerts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [addOpen, setAddOpen] = useState(false);
    const [targetFor, setTargetFor] = useState(null);
    const [showAlerts, setShowAlerts] = useState(false);

    // Add-form state: url is the only required entry; rest auto-fills from scrape
    const [f, setF] = useState({ url: "", title: "", price: "", currency: "USD", image_url: "", size: "", color: "", source: "" });
    const [fetching, setFetching] = useState(false);
    const [fetched, setFetched] = useState(false);       // true once /scrape-url has responded
    const [fetchWarning, setFetchWarning] = useState(""); // shown when scrape returned partial data

    const load = async () => {
        try {
            const [it, al] = await Promise.all([api.get("/wishlist"), api.get("/wishlist/alerts", { params: { only_unseen: true } })]);
            setItems(it.data);
            setAlerts(al.data);
        } catch (e) {
            toast.error("Failed to load wishlist");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const resetAddForm = () => {
        setF({ url: "", title: "", price: "", currency: "USD", image_url: "", size: "", color: "", source: "" });
        setFetched(false);
        setFetchWarning("");
        setFetching(false);
    };

    const fetchUrl = async (rawUrl) => {
        const url = (rawUrl || f.url).trim();
        if (!url) { toast.error("Paste a product URL first"); return; }
        if (!/^https?:\/\//i.test(url)) { toast.error("URL must start with http:// or https://"); return; }
        setFetching(true);
        setFetchWarning("");
        try {
            const { data } = await api.post("/wishlist/scrape-url", { url });
            setF((prev) => ({
                ...prev,
                url: data.url || url,
                title: data.title || "",
                price: data.price != null ? String(data.price) : "",
                currency: data.currency || "USD",
                image_url: data.image_url || "",
                source: data.source || "",
            }));
            setFetched(true);
            if (data.warning) setFetchWarning(data.warning);
            else if (data.needs_price) setFetchWarning("Price wasn't on the page — type it below.");
            else toast.success("Got it — review and add");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't read this page");
            setFetched(true); // let the user still fill it in manually
            setFetchWarning("Fill in the fields below manually.");
        } finally {
            setFetching(false);
        }
    };

    const submitAdd = async (e) => {
        e.preventDefault();
        if (!f.url || !f.title || !f.price) { toast.error("URL, title and price are required"); return; }
        try {
            await api.post("/wishlist/add", { ...f, price: parseFloat(f.price) });
            toast.success("Added to Radar");
            setAddOpen(false);
            resetAddForm();
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Add failed");
        }
    };

    const del = async (id) => {
        if (!window.confirm("Stop tracking this item?")) return;
        await api.delete(`/wishlist/${id}`);
        setItems((rows) => rows.filter((r) => r.id !== id));
    };

    const forceCheck = async (item) => {
        toast.info("Open the item's tab in Chrome — the extension refreshes on visit. Or paste a new price manually via Set target.");
        window.open(item.url, "_blank", "noopener,noreferrer");
    };

    const saveTarget = async (id, tp, pct) => {
        const body = {};
        if (tp !== "" && tp !== null) body.target_price = parseFloat(tp);
        if (pct !== "" && pct !== null) body.target_pct = parseFloat(pct);
        await api.post(`/wishlist/${id}/target`, body);
        toast.success("Target saved");
        setTargetFor(null);
        load();
    };

    const dismissAlerts = async () => {
        await api.post("/wishlist/alerts/mark-seen");
        setAlerts([]);
        setShowAlerts(false);
    };

    const totalTracked = items.length;
    const totalDropped = useMemo(() => items.filter((i) => i.current_price < i.baseline_price).length, [items]);

    return (
        <div className="min-h-screen bg-neutral-50">
            <header className="border-b border-neutral-200 bg-white sticky top-0 z-30">
                <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between">
                    <div>
                        <div className="text-[10px] uppercase tracking-widest text-neutral-500">Personal Price Radar</div>
                        <h1 className="font-display text-2xl tracking-tight leading-none mt-0.5">Wishlist</h1>
                    </div>
                    <div className="flex items-center gap-2">
                        <Link to="/brands" className="border border-black px-3 h-10 flex items-center gap-2 hover:bg-black hover:text-white transition-colors" data-testid="open-brands">
                            <Store className="h-4 w-4" strokeWidth={1.5} />
                            <span className="uppercase tracking-widest text-[10px] font-semibold hidden sm:inline">Brands</span>
                        </Link>
                        <Link to="/mobile" className="border border-black px-3 h-10 flex items-center gap-2 hover:bg-black hover:text-white transition-colors" data-testid="open-mobile">
                            <Smartphone className="h-4 w-4" strokeWidth={1.5} />
                            <span className="uppercase tracking-widest text-[10px] font-semibold hidden sm:inline">Phone</span>
                        </Link>
                        <Link to="/deals" className="relative border border-black bg-yellow-300 text-black px-3 h-10 flex items-center gap-2 hover:bg-yellow-400" data-testid="open-deals">
                            <Flame className="h-4 w-4" strokeWidth={1.5} />
                            <span className="uppercase tracking-widest text-[10px] font-semibold">Deals</span>
                            {totalDropped > 0 && (
                                <span className="absolute -top-2 -right-2 bg-black text-yellow-300 text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center tabular-nums">{totalDropped}</span>
                            )}
                        </Link>
                        <button onClick={() => setShowAlerts(true)} className="relative border border-black px-3 h-10 flex items-center gap-2 hover:bg-black hover:text-white transition-colors" data-testid="open-alerts">
                            <Bell className="h-4 w-4" strokeWidth={1.5} />
                            <span className="uppercase tracking-widest text-[10px] font-semibold">Alerts</span>
                            {alerts.length > 0 && (
                                <span className="absolute -top-2 -right-2 bg-red-600 text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center tabular-nums">{alerts.length}</span>
                            )}
                        </button>
                        <button onClick={() => setAddOpen(true)} className="bg-black text-white px-3 h-10 flex items-center gap-2 hover:bg-neutral-800" data-testid="open-add">
                            <Plus className="h-4 w-4" strokeWidth={1.5} />
                            <span className="uppercase tracking-widest text-[10px] font-semibold">Add Item</span>
                        </button>
                        <button onClick={logout} className="border border-neutral-300 px-3 h-10 hover:border-black" title="Sign out" data-testid="signout">
                            <LogOut className="h-4 w-4" strokeWidth={1.5} />
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-5 py-6">
                {alerts.length > 0 && (
                    <div className="mb-6 border-2 border-red-700 bg-red-50 px-4 py-3 flex items-start gap-3" data-testid="alert-banner">
                        <TrendingDown className="h-5 w-5 text-red-700 shrink-0 mt-0.5" strokeWidth={1.5} />
                        <div className="flex-1 min-w-0">
                            <div className="font-body text-sm">
                                <span className="font-semibold text-red-700">{alerts.length} price drop{alerts.length === 1 ? "" : "s"}</span> since your last visit —{" "}
                                <button onClick={() => setShowAlerts(true)} className="underline decoration-red-700 underline-offset-2 hover:text-red-700" data-testid="alerts-see-all">see all</button>
                                {" · "}
                                <button onClick={dismissAlerts} className="text-neutral-500 hover:text-black">dismiss</button>
                            </div>
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-3 gap-2 mb-6 text-center border border-neutral-200 bg-white">
                    <div className="py-3 border-r border-neutral-200">
                        <div className="font-display text-2xl tabular-nums" data-testid="stat-total">{totalTracked}</div>
                        <div className="text-[10px] uppercase tracking-widest text-neutral-500">tracked</div>
                    </div>
                    <div className="py-3 border-r border-neutral-200">
                        <div className="font-display text-2xl tabular-nums text-green-700" data-testid="stat-dropped">{totalDropped}</div>
                        <div className="text-[10px] uppercase tracking-widest text-neutral-500">on sale</div>
                    </div>
                    <div className="py-3">
                        <div className="font-display text-2xl tabular-nums" data-testid="stat-alerts">{alerts.length}</div>
                        <div className="text-[10px] uppercase tracking-widest text-neutral-500">alerts</div>
                    </div>
                </div>

                {loading && <div className="text-sm text-neutral-500 font-body">Loading…</div>}

                {!loading && items.length === 0 && (
                    <div className="border border-dashed border-neutral-300 p-12 text-center bg-white">
                        <div className="text-[10px] uppercase tracking-widest text-neutral-500">Empty</div>
                        <div className="font-display text-2xl mt-2">Nothing on the radar yet</div>
                        <div className="text-sm text-neutral-500 mt-2 font-body max-w-md mx-auto">
                            Install the Chrome extension, then tap its icon on any product page. Or click <span className="font-semibold">Add Item</span> above and paste the URL + price manually.
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {items.map((it) => {
                        const drop = pct(it.current_price, it.baseline_price);
                        const isDown = it.current_price < it.baseline_price;
                        const hitTarget = it.target_price && it.current_price <= it.target_price;
                        return (
                            <div key={it.id} className={`bg-white border ${hitTarget ? "border-green-700 ring-2 ring-green-200" : "border-neutral-200"}`} data-testid={`item-${it.id}`}>
                                <a href={it.url} target="_blank" rel="noopener noreferrer" className="block aspect-[4/5] bg-neutral-50 border-b border-neutral-200 overflow-hidden relative">
                                    {it.image_url ? (
                                        <img src={it.image_url} alt={it.title} loading="lazy" decoding="async" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-neutral-300 text-4xl font-display">?</div>
                                    )}
                                    <div className="absolute top-2 left-2 flex flex-col gap-1">
                                        <span className="text-[9px] uppercase tracking-widest bg-white/90 border border-neutral-300 px-1.5 py-0.5">{it.source}</span>
                                        {(it.size || it.color) && (
                                            <span className="text-[9px] uppercase tracking-widest bg-blue-50/90 border border-blue-200 text-blue-800 px-1.5 py-0.5 font-semibold" data-testid={`variant-${it.id}`}>
                                                {[it.size && `size ${it.size}`, it.color].filter(Boolean).join(" · ")}
                                            </span>
                                        )}
                                        {isDown && (
                                            <span className="text-[9px] uppercase tracking-widest bg-green-700 text-white px-1.5 py-0.5 font-semibold">-{drop}%</span>
                                        )}
                                        {!it.in_stock && (
                                            <span className="text-[9px] uppercase tracking-widest bg-neutral-800 text-white px-1.5 py-0.5">out of stock</span>
                                        )}
                                    </div>
                                </a>
                                <div className="p-3">
                                    <div className="font-body text-sm line-clamp-2 min-h-[2.5em]">{it.title}</div>
                                    <div className="flex items-end justify-between mt-2">
                                        <div>
                                            <div className="font-display text-2xl tabular-nums leading-none">
                                                {curSymbol(it.currency)}{it.current_price?.toFixed(2)}
                                            </div>
                                            {isDown && (
                                                <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1 line-through">was {curSymbol(it.currency)}{it.baseline_price?.toFixed(2)}</div>
                                            )}
                                            {it.target_price && !hitTarget && (
                                                <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1">target {curSymbol(it.currency)}{it.target_price?.toFixed(2)}</div>
                                            )}
                                            {hitTarget && (
                                                <div className="text-[10px] uppercase tracking-widest text-green-700 mt-1 font-semibold">🎯 target hit</div>
                                            )}
                                        </div>
                                        <Sparkline points={it.price_history} color={isDown ? "#15803D" : "#0A0A0A"} />
                                    </div>
                                    <div className="text-[10px] uppercase tracking-widest text-neutral-400 mt-2">
                                        {/* Variant chip is already in the image overlay — this row just shows the last-checked time */}
                                        checked {daysAgo(it.last_checked_at)}
                                    </div>
                                    <div className="flex items-center gap-1 mt-3">
                                        <button onClick={() => setTargetFor(it)} className="text-[10px] uppercase tracking-widest border border-black px-2 h-8 hover:bg-black hover:text-white flex items-center gap-1" data-testid={`target-${it.id}`}>
                                            <Target className="h-3 w-3" strokeWidth={1.5} /> Target
                                        </button>
                                        <button onClick={() => forceCheck(it)} className="text-[10px] uppercase tracking-widest border border-neutral-300 px-2 h-8 hover:border-black flex items-center gap-1" data-testid={`open-${it.id}`}>
                                            <ExternalLink className="h-3 w-3" strokeWidth={1.5} /> Open
                                        </button>
                                        <div className="flex-1" />
                                        <button onClick={() => del(it.id)} className="border border-neutral-300 h-8 w-8 flex items-center justify-center hover:border-red-700 hover:text-red-700" data-testid={`del-${it.id}`}>
                                            <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </main>

            {addOpen && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => { setAddOpen(false); resetAddForm(); }}>
                    <div className="bg-white max-w-md w-full max-h-[92vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="font-display text-2xl">Add to Radar</h2>
                            <button onClick={() => { setAddOpen(false); resetAddForm(); }} data-testid="add-close"><X className="h-5 w-5" /></button>
                        </div>

                        {/* STEP 1 — paste URL, click Fetch */}
                        <div className="space-y-2 mb-4">
                            <div className="text-[10px] uppercase tracking-widest text-neutral-500">Product URL</div>
                            <div className="flex gap-2">
                                <div className="flex items-center border border-neutral-300 flex-1 h-11 px-3 focus-within:border-black">
                                    <LinkIcon className="h-4 w-4 text-neutral-400 shrink-0 mr-2" strokeWidth={1.5} />
                                    <input
                                        value={f.url}
                                        onChange={(e) => { setF({ ...f, url: e.target.value }); if (fetched) { setFetched(false); setFetchWarning(""); } }}
                                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); fetchUrl(); } }}
                                        onPaste={(e) => {
                                            // Auto-fetch on paste for the ultimate one-click flow
                                            const pasted = e.clipboardData?.getData("text") || "";
                                            if (/^https?:\/\//i.test(pasted.trim())) {
                                                setTimeout(() => fetchUrl(pasted.trim()), 50);
                                            }
                                        }}
                                        className="w-full h-full outline-none font-body text-sm bg-transparent"
                                        placeholder="Paste any Nike / Macy's / Nordstrom link…"
                                        data-testid="add-url"
                                        autoFocus
                                    />
                                </div>
                                <button
                                    type="button"
                                    onClick={() => fetchUrl()}
                                    disabled={fetching || !f.url.trim()}
                                    className="bg-black text-white h-11 px-4 uppercase tracking-widest text-[10px] font-semibold hover:bg-neutral-800 disabled:bg-neutral-300 flex items-center gap-2"
                                    data-testid="add-fetch"
                                >
                                    {fetching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" strokeWidth={1.5} />}
                                    {fetching ? "Reading" : "Fetch"}
                                </button>
                            </div>
                            <p className="text-[10px] text-neutral-400 font-body">
                                We read the title, price and image from the page — no scraper key needed.
                            </p>
                        </div>

                        {/* STEP 2 — preview + confirm (only after Fetch) */}
                        {fetched && (
                            <form onSubmit={submitAdd} className="space-y-3 border-t border-neutral-200 pt-4">
                                {fetchWarning && (
                                    <div className="border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-body text-amber-800" data-testid="fetch-warning">
                                        {fetchWarning}
                                    </div>
                                )}

                                {/* Live preview card */}
                                {(f.image_url || f.title) && (
                                    <div className="border border-neutral-200 bg-neutral-50 p-3 flex gap-3" data-testid="add-preview">
                                        {f.image_url ? (
                                            <img src={f.image_url} alt="" className="w-20 h-24 object-cover border border-neutral-200" loading="lazy" />
                                        ) : (
                                            <div className="w-20 h-24 border border-dashed border-neutral-300 flex items-center justify-center text-neutral-300 text-2xl">?</div>
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <div className="text-[9px] uppercase tracking-widest text-neutral-500">{f.source || "preview"}</div>
                                            <div className="font-body text-sm line-clamp-2 mt-0.5">{f.title || "(no title found)"}</div>
                                            {f.price && (
                                                <div className="font-display text-xl tabular-nums mt-1">
                                                    {curSymbol(f.currency)}{parseFloat(f.price).toFixed(2)}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                <label className="block">
                                    <span className="text-[10px] uppercase tracking-widest text-neutral-500">Title</span>
                                    <input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="w-full border border-neutral-300 h-10 px-3 mt-1 font-body text-sm" data-testid="add-title" />
                                </label>
                                <div className="grid grid-cols-2 gap-3">
                                    <label className="block">
                                        <span className="text-[10px] uppercase tracking-widest text-neutral-500">Current price</span>
                                        <input required type="number" step="0.01" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} className="w-full border border-neutral-300 h-10 px-3 mt-1 font-body text-sm" data-testid="add-price" />
                                    </label>
                                    <label className="block">
                                        <span className="text-[10px] uppercase tracking-widest text-neutral-500">Currency</span>
                                        <select value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value })} className="w-full border border-neutral-300 h-10 px-3 mt-1 font-body text-sm">
                                            <option value="USD">USD $</option>
                                            <option value="GBP">GBP £</option>
                                            <option value="EUR">EUR €</option>
                                        </select>
                                    </label>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <input value={f.size} onChange={(e) => setF({ ...f, size: e.target.value })} placeholder="Size (opt)" className="border border-neutral-300 h-10 px-3 font-body text-sm" data-testid="add-size" />
                                    <input value={f.color} onChange={(e) => setF({ ...f, color: e.target.value })} placeholder="Color (opt)" className="border border-neutral-300 h-10 px-3 font-body text-sm" data-testid="add-color" />
                                </div>
                                <details className="text-xs">
                                    <summary className="cursor-pointer text-neutral-500 hover:text-black uppercase tracking-widest text-[10px]">Image URL (advanced)</summary>
                                    <input value={f.image_url} onChange={(e) => setF({ ...f, image_url: e.target.value })} className="w-full border border-neutral-300 h-10 px-3 mt-2 font-body text-sm" placeholder="Auto-filled from the page" />
                                </details>
                                <button type="submit" className="w-full bg-black text-white h-11 uppercase tracking-widest text-[11px] font-semibold hover:bg-neutral-800" data-testid="add-submit">
                                    Add to Radar
                                </button>
                            </form>
                        )}

                        {!fetched && !fetching && (
                            <div className="border border-dashed border-neutral-200 px-4 py-6 text-center text-xs font-body text-neutral-400">
                                Paste a URL above and hit <span className="uppercase tracking-widest">Fetch</span> — or press Enter.
                            </div>
                        )}
                    </div>
                </div>
            )}

            {targetFor && (
                <TargetModal item={targetFor} onClose={() => setTargetFor(null)} onSave={saveTarget} />
            )}

            {showAlerts && (
                <AlertsPanel alerts={alerts} onClose={() => setShowAlerts(false)} onDismiss={dismissAlerts} />
            )}
        </div>
    );
}

function TargetModal({ item, onClose, onSave }) {
    const [tp, setTp] = useState(item.target_price ?? "");
    const [tpc, setTpc] = useState(item.target_pct ?? "");
    return (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white max-w-sm w-full p-5" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <h2 className="font-display text-xl">Alert me when…</h2>
                    <button onClick={onClose}><X className="h-5 w-5" /></button>
                </div>
                <div className="text-xs font-body text-neutral-500 mb-3">
                    Current price {curSymbol(item.currency)}{item.current_price?.toFixed(2)} · baseline {curSymbol(item.currency)}{item.baseline_price?.toFixed(2)}
                </div>
                <div className="space-y-3">
                    <label className="block">
                        <span className="text-[10px] uppercase tracking-widest text-neutral-500">Absolute target price ({curSymbol(item.currency)})</span>
                        <input type="number" step="0.01" value={tp} onChange={(e) => setTp(e.target.value)} className="w-full border border-neutral-300 h-10 px-3 mt-1 font-body text-sm" data-testid="target-price-input" />
                    </label>
                    <div className="text-center text-[10px] uppercase tracking-widest text-neutral-400">OR</div>
                    <label className="block">
                        <span className="text-[10px] uppercase tracking-widest text-neutral-500">% drop off baseline</span>
                        <input type="number" step="1" value={tpc} onChange={(e) => setTpc(e.target.value)} className="w-full border border-neutral-300 h-10 px-3 mt-1 font-body text-sm" placeholder="e.g. 20" />
                    </label>
                    <button onClick={() => onSave(item.id, tp, tpc)} className="w-full bg-black text-white h-11 uppercase tracking-widest text-[11px] font-semibold hover:bg-neutral-800" data-testid="target-save">Save</button>
                </div>
            </div>
        </div>
    );
}

function AlertsPanel({ alerts, onClose, onDismiss }) {
    return (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-end p-0 sm:p-4" onClick={onClose}>
            <div className="bg-white max-w-md w-full h-full sm:h-auto sm:max-h-[85vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <h2 className="font-display text-2xl">Price drops</h2>
                    <button onClick={onClose}><X className="h-5 w-5" /></button>
                </div>
                {alerts.length === 0 ? (
                    <div className="text-sm text-neutral-500 font-body">Nothing new — check back tomorrow.</div>
                ) : (
                    <>
                        <button onClick={onDismiss} className="mb-3 text-[10px] uppercase tracking-widest border border-black px-3 h-8 hover:bg-black hover:text-white" data-testid="alerts-mark-all">Mark all as seen</button>
                        <div className="space-y-3">
                            {alerts.map((a) => (
                                <a key={a.id} href={a.url} target="_blank" rel="noopener noreferrer" className="block border border-neutral-200 p-3 hover:border-black" data-testid={`alert-${a.id}`}>
                                    <div className="flex gap-3">
                                        {a.image_url && <img src={a.image_url} alt="" className="w-16 h-20 object-cover border border-neutral-200" loading="lazy"/>}
                                        <div className="min-w-0 flex-1">
                                            <div className="text-[9px] uppercase tracking-widest text-neutral-500">{a.source}</div>
                                            <div className="font-body text-sm line-clamp-2">{a.title}</div>
                                            <div className="mt-2 flex items-baseline gap-2">
                                                <span className="font-display text-lg tabular-nums text-green-700">{curSymbol(a.currency)}{a.new_price?.toFixed(2)}</span>
                                                <span className="text-xs tabular-nums text-neutral-400 line-through">{curSymbol(a.currency)}{a.prev_price?.toFixed(2)}</span>
                                                <span className="text-xs uppercase tracking-widest text-green-700 font-semibold">-{a.dropped_pct}%</span>
                                            </div>
                                            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1">{a.reason}</div>
                                        </div>
                                    </div>
                                </a>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
